/**
 * Landing Page Configuration
 * This file contains all the content data for the Landing page.
 * Edit this file to update landing page content without modifying component code.
 */

// Subscription tier styling and labels
export const TIER_CONFIG = {
  free: {
    label: '⭐ Free',
    style: 'bg-gray-200 text-gray-800 border-gray-400',
    bgGradient: 'from-gray-100 to-gray-50',
    borderColor: 'border-gray-400',
    title: '⭐ Free Tier - Start Your Journey',
    description: 'Essential tools to get your family connected - no cost, no commitment'
  },
  rooted: {
    label: '🌱 Rooted',
    style: 'bg-emerald-100 text-emerald-800 border-emerald-500',
    bgGradient: 'from-emerald-100 to-emerald-50',
    borderColor: 'border-emerald-500',
    title: '🌱 Rooted Tier - Plant Your Foundation',
    description: 'Build structure and organization for your family\'s future'
  },
  growing: {
    label: '🌿 Growing',
    style: 'bg-blue-100 text-blue-800 border-blue-500',
    bgGradient: 'from-blue-100 to-blue-50',
    borderColor: 'border-blue-500',
    title: '🌿 Growing Tier - Expand Your Reach',
    description: 'Deepen connections with enhanced communication tools'
  },
  flourishing: {
    label: '🌳 Flourishing',
    style: 'bg-amber-100 text-amber-800 border-amber-500',
    bgGradient: 'from-amber-100 to-amber-50',
    borderColor: 'border-amber-500',
    title: '🌳 Flourishing Tier - Full Legacy Experience',
    description: 'Complete access to premium features and professional network'
  }
};

// Hero section content
export const HERO_CONTENT = {
  title: 'Build Your Family Legacy Together',
  subtitle: 'Unite your family with powerful tools for reunion planning, governance, financial empowerment, mentorship, and legacy building. Preserve your heritage, strengthen bonds, and grow together across generations through faith, education, and intentional design.',
  primaryCTA: 'Get Started Free',
  secondaryCTA: 'View Pricing',
  highlights: [
    { icon: '💰', label: 'Wealth Building', color: 'text-blue-600' },
    { icon: '🌱', label: 'Mentorship', color: 'text-purple-600' },
    { icon: '✨', label: 'Spiritual Legacy', color: 'text-amber-600' }
  ]
};

// Core features organized by tier
export const CORE_FEATURES = {
  free: [
    {
      id: 'reunion-planning',
      icon: 'FaCalendarAlt',
      title: 'Reunion Planning',
      description: 'Create events, manage RSVPs, and coordinate all the details for unforgettable family gatherings.'
    },
    {
      id: 'task-management',
      icon: 'FaTasks',
      title: 'Task Delegation',
      description: 'Assign and track tasks for meals, transportation, lodging, and activities with ease.'
    },
    {
      id: 'photo-sharing',
      icon: 'FaCamera',
      title: 'Photo Gallery',
      description: 'Share and archive precious family moments from every gathering and milestone.'
    },
    {
      id: 'family-chat',
      icon: 'FaComments',
      title: 'Family Chat & Messaging',
      description: 'Stay connected every day with family group chat and direct messages — reunions shouldn\'t only happen once a year.'
    }
  ],
  rooted: [
    {
      id: 'family-governance',
      icon: 'FaUsers',
      title: 'Family Governance',
      description: 'Assign roles like Vision Keeper, Scribe, and Harvest Guardian to organize your family leadership.'
    },
    {
      id: 'budget-tracking',
      icon: 'FaMoneyBillWave',
      title: 'Budget Tracking',
      description: 'Track shared expenses and contributions transparently for all family events.'
    }
  ],
  growing: [
    {
      id: 'priority-messages',
      icon: 'FaComments',
      title: 'Priority Messages',
      description: 'Pinned announcements and priority delivery so important family updates never get lost in the thread.'
    }
  ]
};

// Wealth & Legacy features
export const WEALTH_FEATURES = {
  rooted: [
    {
      icon: '📚',
      title: 'Financial Resource Education',
      description: 'Access information on family trusts, insurance, homeownership, and investing to make informed financial decisions.'
    }
  ],
  flourishing: [
    {
      icon: '🤝',
      title: 'Connect with Professionals',
      description: 'Get connected with vetted financial advisors, attorneys, and CPAs who understand your family\'s needs.'
    },
    {
      icon: '🎓',
      title: 'Family Consultations',
      description: 'Join invite-only webinars and family-specific consultations to strengthen your financial foundation.'
    }
  ]
};

// Professional categories for the professionals section
export const PROFESSIONAL_CATEGORIES = [
  {
    id: 'financial-advisors',
    icon: '💼',
    title: 'Financial Advisors',
    description: 'CFPs and wealth managers who specialize in family financial planning and intergenerational wealth transfer.',
    gradient: 'from-blue-50 to-indigo-50',
    iconBg: 'bg-blue-100',
    services: ['Retirement Planning', 'Investment Strategies', 'Tax Optimization']
  },
  {
    id: 'estate-attorneys',
    icon: '⚖️',
    title: 'Estate Attorneys',
    description: 'Legal experts who help establish trusts, wills, and protect your family\'s assets for generations.',
    gradient: 'from-purple-50 to-pink-50',
    iconBg: 'bg-purple-100',
    services: ['Trust Formation', 'Estate Planning', 'Asset Protection']
  },
  {
    id: 'cpas',
    icon: '📊',
    title: 'CPAs & Tax Experts',
    description: 'Accountants who understand family businesses and can maximize tax efficiency across generations.',
    gradient: 'from-green-50 to-emerald-50',
    iconBg: 'bg-green-100',
    services: ['Tax Planning', 'Business Structures', 'Wealth Transfer']
  },
  {
    id: 'real-estate',
    icon: '🏠',
    title: 'Real Estate Experts',
    description: 'Property specialists who help families build wealth through smart real estate investments.',
    gradient: 'from-amber-50 to-orange-50',
    iconBg: 'bg-amber-100',
    services: ['Property Investment', 'Multi-generational Homes', 'Rental Strategies']
  }
];

// Consultation types
export const CONSULTATION_TYPES = [
  {
    id: 'webinars',
    icon: '📺',
    title: 'Live Webinars',
    subtitle: 'Monthly sessions',
    subtitleColor: 'text-indigo-600',
    iconBg: 'bg-indigo-100',
    description: 'Interactive online sessions with industry experts on topics from estate planning to family business succession.',
    upcomingSessions: [
      { title: 'Building Your Family Trust', date: 'Mar 15', status: 'green' },
      { title: 'Tax Strategies for Families', date: 'Apr 5', status: 'yellow' },
      { title: 'Real Estate Investing 101', date: 'Apr 20', status: 'blue' }
    ]
  },
  {
    id: 'one-on-one',
    icon: '👥',
    title: '1-on-1 Family Sessions',
    subtitle: 'Personalized guidance',
    subtitleColor: 'text-purple-600',
    iconBg: 'bg-purple-100',
    description: 'Private consultation with a family wealth specialist tailored to your unique situation and goals.',
    features: [
      '60-minute private session',
      'Custom action plan',
      'Follow-up resources',
      'Family member participation'
    ],
    popular: true
  },
  {
    id: 'workshops',
    icon: '🏫',
    title: 'Family Workshops',
    subtitle: 'Quarterly events',
    subtitleColor: 'text-green-600',
    iconBg: 'bg-green-100',
    description: 'In-depth workshops where multiple family members can learn and plan together with expert facilitation.',
    upcomingWorkshop: {
      title: 'Family Governance & Decision Making',
      date: 'March 22-23, 2026',
      format: 'Virtual'
    }
  }
];

// Mentorship & Spiritual features
export const MENTORSHIP_FEATURES = [
  {
    id: 'legacy-stories',
    icon: '📖',
    title: 'Legacy Stories',
    description: 'Preserve and share family history, wisdom, and traditions through digital storytelling.',
    tier: 'rooted'
  },
  {
    id: 'faith-journey',
    icon: '🙏',
    title: 'Faith Journey',
    description: 'Track and share spiritual milestones, prayers, and gratitude across your family.',
    tier: 'growing'
  },
  {
    id: 'mentorship-circles',
    icon: '🎯',
    title: 'Mentorship Circles',
    description: 'Connect experienced family members with younger generations for guidance and support.',
    tier: 'flourishing'
  },
  {
    id: 'family-courses',
    icon: '🎓',
    title: 'Family Courses',
    description: 'Access curated educational content on leadership, finance, and family values.',
    tier: 'flourishing'
  }
];

// Testimonials
export const TESTIMONIALS = [
  {
    id: 1,
    quote: "Legacy Forge Family helped us plan our best reunion yet! The task delegation and RSVP features made everything so organized.",
    author: 'The Johnson Family',
    location: 'Atlanta, GA',
    avatar: '👨‍👩‍👧‍👦'
  },
  {
    id: 2,
    quote: "Finally, a platform that understands the importance of family governance. The role system is brilliant for organizing our family council.",
    author: 'The Williams Family',
    location: 'Chicago, IL',
    avatar: '👨‍👩‍👦‍👦'
  },
  {
    id: 3,
    quote: "The wealth building resources and professional connections have been invaluable. Our family is finally having real conversations about generational wealth.",
    author: 'The Davis Family',
    location: 'Houston, TX',
    avatar: '👨‍👨‍👧‍👧'
  }
];

// Stats for the stats section
export const PLATFORM_STATS = [
  { value: '1,000+', label: 'Families Connected' },
  { value: '50+', label: 'Reunions Planned' },
  { value: '10,000+', label: 'Photos Shared' },
  { value: '98%', label: 'Satisfaction Rate' }
];

// CTA section content
export const CTA_CONTENT = {
  title: 'Start Building Your Family Legacy Today',
  subtitle: 'Join thousands of families using Legacy Forge Family to strengthen bonds, preserve heritage, and plan for generations to come.',
  primaryCTA: 'Create Your Family',
  secondaryCTA: 'View Pricing'
};

// Footer links
export const FOOTER_LINKS = {
  product: [
    { label: 'Features', path: '/pricing' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Testimonials', path: '/#testimonials' }
  ],
  company: [
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Careers', path: '/careers' }
  ],
  legal: [
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Terms of Service', path: '/terms' }
  ],
  social: [
    { label: 'Twitter', url: 'https://twitter.com' },
    { label: 'Facebook', url: 'https://facebook.com' },
    { label: 'Instagram', url: 'https://instagram.com' }
  ]
};
